import type {
  Segment,
  SegmentApiError,
  SegmentConflictResponse
} from '@/services/segment.service'

const DISALLOWED_SEGMENT_CHARACTERS = /[^A-Za-z0-9 äöüÄÖÜß-]/g

export const sanitizeSegmentInput = (input: string) =>
  input.replace(DISALLOWED_SEGMENT_CHARACTERS, '')

export const filterSegments = (
  segments: Segment[],
  selectedValues: string[],
  query: string
) => {
  const normalizedQuery = query.trim().toLocaleLowerCase()

  return segments.filter(
    ({ label, value }) =>
      !selectedValues.includes(value) &&
      (!normalizedQuery ||
        label.toLocaleLowerCase().includes(normalizedQuery) ||
        value.toLocaleLowerCase().includes(normalizedQuery))
  )
}

export const shouldShowCreateSegment = (
  input: string,
  matchingSegments: Segment[]
) => Boolean(input.trim()) && matchingSegments.length === 0

export const addSegmentValue = (values: string[], value: string) =>
  values.includes(value) ? values : [...values, value]

export const removeSegmentValue = (values: string[], value: string) =>
  values.filter((selectedValue) => selectedValue !== value)

export const getConflictSegment = (
  error: SegmentApiError
): Segment | undefined => {
  if (error.response?.status !== 409) return undefined

  const responseData = error.response.data

  if (
    responseData &&
    'segment' in responseData &&
    (responseData as SegmentConflictResponse).segment
  ) {
    return (responseData as SegmentConflictResponse).segment
  }

  if (responseData && 'value' in responseData && 'label' in responseData) {
    return responseData as Segment
  }

  return undefined
}

export const getSegmentErrorMessage = (error: SegmentApiError) => {
  const responseData = error.response?.data

  if (responseData && 'error' in responseData && responseData.error) {
    return responseData.error
  }

  if (responseData && 'message' in responseData && responseData.message) {
    return responseData.message
  }

  return error.message || 'Unable to create segment'
}

const areSegmentsEqual = (first: string[], second: string[]) =>
  [...first].sort().join('\u0000') === [...second].sort().join('\u0000')

export const withChangedSegments = <T extends { segments?: string[] }>(
  organization: T,
  selectedSegments: string[],
  initialSegments: string[]
): T => {
  const payload = { ...organization }

  delete payload.segments

  if (!areSegmentsEqual(selectedSegments, initialSegments)) {
    payload.segments = selectedSegments
  }

  return payload
}
